import React from "react";
import {
  useQuery,
  hashQueryKey,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
  useQueryClient,
} from "react-query";
import {
  onSnapshot,
  doc,
  collection,
  query,
  where,
  orderBy,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  getDocs,
  increment,
  runTransaction,
  writeBatch,
  Timestamp,
  limit,
} from "firebase/firestore";
import { db, auth } from "../config/firebase";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from "./auth";

const functions = getFunctions();

// React Query client
const client = new QueryClient();

/**** USERS ****/

// Subscribe to user data
export function useUser(uid) {
  return useQuery(
    ["user", { uid }],
    async () => {
      try {
        if (!uid) return null;
        const userDoc = await getDoc(doc(db, "users", uid));
        return userDoc.exists() ? format(userDoc) : null;
      } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
      }
    },
    {
      enabled: !!uid,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );
}

// Fetch user data once
export function getUser(uid) {
  return getDoc(doc(db, "users", uid)).then(format);
}

// Create a new user
export function createUser(uid, data) {
  return setDoc(doc(db, "users", uid), data, { merge: true });
}

// Update an existing user
export function updateUser(uid, data) {
  return updateDoc(doc(db, "users", uid), data);
}

// Dismiss walkthrough for user
export function dismissWalkthrough(uid) {
  return updateDoc(doc(db, "users", uid), {
    hasSeenWalkthrough: true
  });
}

// Reset walkthrough for user (allows them to see it again)
export function resetWalkthrough(uid) {
  return updateDoc(doc(db, "users", uid), {
    hasSeenWalkthrough: false
  });
}

// Get user stats
export function useUserStats(uid) {
  return useQuery(
    ["userStats", { uid }],
    createQuery(() => doc(db, "users", uid, "stats", "default")),
    { enabled: !!uid }
  );
}

// Format user stats data
const formatUserStatsData = (doc) => {
  if (!doc) return null;
  const data = doc.data ? doc.data() : doc;
  return {
    id: doc.id || data.id,
    email: data.email,
    photoURL: data.photoURL,
    stats: {
      total_bonus: parseFloat(data.stats?.total_bonus) || 0,
      completed_bonuses: parseInt(data.stats?.completed_bonuses) || 0,
      ...data.stats
    }
  };
};

// Create a reusable query function
const createQuery = (queryFn, formatter = format) => {
  return async () => {
    try {
      if (typeof queryFn === "function") {
        const ref = queryFn();
        if (ref.type === "document") {
          const doc = await getDoc(ref);
          return doc.exists() ? formatter(doc) : null;
        }
        const snapshot = await getDocs(ref);
        return snapshot.docs.map(formatter);
      }
      const snapshot = await getDocs(queryFn);
      return snapshot.docs.map(formatter);
    } catch (error) {
      console.error("Query error:", error);
      throw error;
    }
  };
}

// Subscribe to friends list
export function useFriends(userId) {
  return useQuery(
    ["friends", { userId }],
    createQuery(
      () => collection(db, "users", userId, "friends"),
      (doc) => ({
        id: doc.id,
        ...doc.data()
      })
    ),
    {
      enabled: !!userId,
    }
  );
}

// Get friend requests
export function useFriendRequests(uid) {
  return useQuery(
    ["friendRequests", { uid }],
    createQuery(() =>
      query(
        collection(db, "users", uid, "friendRequests"),
        where("status", "==", "pending"),
        orderBy("createdAt", "desc")
      )
    ),
    { enabled: !!uid }
  );
}

// Send friend request
export async function sendFriendRequest(toEmail) {
  try {
    if (!toEmail || typeof toEmail !== 'string') {
      throw new Error("Invalid email address");
    }

    if (!auth.currentUser?.uid) {
      throw new Error("You must be logged in to send friend requests");
    }

    if (toEmail === auth.currentUser.email) {
      throw new Error("You cannot send a friend request to yourself");
    }

    // Query for user with the given email
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", toEmail.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // User doesn't exist - create a pending invite
      const pendingInvitesRef = collection(db, "pendingInvites");
      const inviteDoc = await addDoc(pendingInvitesRef, {
        toEmail: toEmail.toLowerCase(),
        fromUserId: auth.currentUser.uid,
        fromUserEmail: auth.currentUser.email,
        status: "pending",
        createdAt: serverTimestamp()
      });

      try {
        // Send invitation email using Firebase Functions
        const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
        await sendInviteEmail({ 
          toEmail: toEmail.toLowerCase(), 
          fromEmail: auth.currentUser.email 
        });
      } catch (emailError) {
        console.error("Error sending invite email:", emailError);
        // Continue even if email fails - the invite is still created
      }

      return { 
        success: true, 
        type: "invite_sent",
        inviteId: inviteDoc.id
      };
    }

    const toUserId = querySnapshot.docs[0].id;
    
    // Check if request already exists
    const existingRequestsRef = collection(db, "users", toUserId, "friendRequests");
    const existingRequestsQuery = query(
      existingRequestsRef, 
      where("fromUserId", "==", auth.currentUser.uid),
      where("status", "==", "pending")
    );
    const existingRequests = await getDocs(existingRequestsQuery);
    
    if (!existingRequests.empty) {
      throw new Error("A friend request to this user is already pending");
    }

    // Add request to recipient's friendRequests collection
    await addDoc(existingRequestsRef, {
      fromUserId: auth.currentUser.uid,
      fromUserEmail: auth.currentUser.email,
      status: "pending",
      createdAt: serverTimestamp()
    });

    return { 
      success: true, 
      type: "request_sent" 
    };
  } catch (error) {
    console.error("Error sending friend request:", error);
    throw error;
  }
}

/**** BANKS ****/

// Subscribe to bank data
export function useBank(bankId) {
  return useQuery(
    ["bank", { bankId }],
    createQuery(() => doc(db, "banks", bankId)),
    { enabled: !!bankId }
  );
}

// Subscribe to banks list
export function useBanks(isAdmin = false) {
  return useQuery(
    ["banks"],
    async () => {
      try {
        const q = isAdmin 
          ? query(collection(db, "banks"))
          : query(
              collection(db, "banks"),
              where("status", "==", "verified")
            );
        
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(formatBankData);
      } catch (error) {
        console.error("Error fetching banks:", error);
        throw error;
      }
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );
}

// Subscribe to banks by owner
export function useBanksByOwner(owner) {
  return useQuery(
    ["banks", { owner }],
    createQuery(() =>
      query(
        collection(db, "banks"),
        where("owner", "==", owner),
        orderBy("createdAt", "desc")
      )
    ),
    { enabled: !!owner }
  );
}

// Format Firestore document helper
const format = (doc) => {
  if (!doc) return null;
  const data = doc.data ? doc.data() : doc;
  return {
    id: doc.id,
    ...data,
    amount: parseFloat(data.amount) || 0,
    days: parseInt(data.days) || 0,
    needed: parseFloat(data.needed) || 0,
    upvotes: data.upvotes || 0,
    downvotes: data.downvotes || 0
  };
};

// Helper function to format currency
export const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Use this function when formatting amounts in queries
const formatBankData = (doc) => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    amount: data.amount ? formatCurrency(data.amount) : '$0',
    needed: data.needed ? formatCurrency(data.needed) : '$0',
    total_bonus: data.total_bonus ? formatCurrency(data.total_bonus) : '$0'
  };
};

// Get banks with bonuses
export function useBanksWithBonuses() {
  const auth = useAuth();
  const isAdmin = auth.user?.email === 'fedify8@gmail.com';
  
  return useQuery(
    ["banks"],
    async () => {
      try {
        console.log('Auth state:', {
          isAuthenticated: !!auth.user,
          userEmail: auth.user?.email,
          isAdmin
        });
        
        let q;
        if (isAdmin) {
          console.log('Using admin query');
          q = query(
            collection(db, "banks"),
            orderBy("createdAt", "desc")
          );
        } else {
          console.log('Using non-admin query');
          q = query(
            collection(db, "banks"),
            where("status", "==", "verified"),
            orderBy("createdAt", "desc")
          );
        }
        
        const snapshot = await getDocs(q);
        console.log('Query results:', {
          totalDocs: snapshot.size,
          empty: snapshot.empty,
          docs: snapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            status: doc.data().status
          }))
        });
        
        const formattedData = snapshot.docs.map(format).filter(Boolean);
        console.log('Formatted data:', {
          length: formattedData.length,
          sample: formattedData[0]
        });
        
        return formattedData;
      } catch (error) {
        console.error('Error fetching banks:', error);
        throw error;
      }
    },
    {
      enabled: !!auth.user,
      staleTime: 5 * 60 * 1000
    }
  );
}

// Create a new bank
export async function createBank(data) {
  try {
    const bankRef = doc(collection(db, "banks"));
    const auth = getAuth();
    
    const bankData = {
      ...data,
      directDeposit: Number(data.directDeposit) || null,
      timeframe: Number(data.timeframe) || null,
      requirements: data.requirements || '',
      upvotes: 0,
      downvotes: 0,
      status: 'pending',
      bookmarkedBy: [],
      owner: auth.currentUser?.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(bankRef, bankData);
    return bankRef.id;
  } catch (error) {
    console.error('Error creating bank:', error);
    throw error;
  }
}

// Update an existing bank
export async function updateBank(bankId, data) {
  try {
    const bankRef = doc(db, "banks", bankId);
    
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    
    if (data.directDeposit !== undefined) {
      updateData.directDeposit = Number(data.directDeposit);
    }
    
    if (data.timeframe !== undefined) {
      updateData.timeframe = Number(data.timeframe);
    }
    
    await updateDoc(bankRef, updateData);
  } catch (error) {
    console.error('Error updating bank:', error);
    throw error;
  }
}

// Delete a bank
export const deleteBank = async (bankId) => {
  try {
    await deleteDoc(doc(db, 'banks', bankId));
  } catch (error) {
    console.error('Error deleting bank:', error);
    throw error;
  }
};

// Handle bank voting
export async function handleBankVote(bankId, userId, voteType) {
  try {
    const bankRef = doc(db, "banks", bankId);
    const userVoteRef = doc(db, "users", userId, "votes", bankId);
    
    // Get current vote if exists
    const voteDoc = await getDoc(userVoteRef);
    const currentVote = voteDoc.exists() ? voteDoc.data().type : null;
    
    // Get current bank data
    const bankDoc = await getDoc(bankRef);
    if (!bankDoc.exists()) {
      throw new Error("Bank not found");
    }
    
    // Start a batch write
    const batch = writeBatch(db);
    
    if (currentVote === voteType) {
      // Remove vote if clicking same button
      batch.delete(userVoteRef);
      batch.update(bankRef, {
        [`${currentVote}s`]: increment(-1)
      });
    } else {
      // Add new vote
      batch.set(userVoteRef, {
        type: voteType,
        timestamp: serverTimestamp()
      });
      
      if (currentVote) {
        // Update both vote counts if changing vote
        batch.update(bankRef, {
          [`${currentVote}s`]: increment(-1),
          [`${voteType}s`]: increment(1)
        });
      } else {
        // Just increment new vote type if no previous vote
        batch.update(bankRef, {
          [`${voteType}s`]: increment(1)
        });
      }
    }
    
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error("Error handling vote:", error);
    throw error;
  }
}

// Verify bank status
export const verifyBank = async (bankId, userId, status) => {
  try {
    await updateDoc(doc(db, 'banks', bankId), {
      status,
      verifiedBy: userId,
      verifiedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error verifying bank:', error);
    throw error;
  }
};

/**** HELPERS ****/

// Create a query with realtime updates
function createRealtimeQuery(queryFn) {
  return () => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onSnapshot(
        typeof queryFn === "function" ? queryFn() : queryFn,
        (snapshot) => {
          if (snapshot.type === "document") {
            resolve(snapshot.exists() ? format(snapshot) : null);
          } else {
            resolve(snapshot.docs.map(format));
          }
        },
        (error) => {
          reject(error);
        }
      );
      // Cleanup subscription on error
      Promise.reject().catch(() => unsubscribe());
    });
  };
}

// Add these new functions
export async function acceptFriendRequest(requestId) {
  try {
    const requestRef = doc(db, "users", auth.currentUser.uid, "friendRequests", requestId);
    const requestDoc = await getDoc(requestRef);
    
    if (!requestDoc.exists()) {
      throw new Error("Friend request not found");
    }

    const requestData = requestDoc.data();
    const fromUserId = requestData.fromUserId;

    // Add each user to the other's friends collection
    await Promise.all([
      setDoc(doc(db, "users", auth.currentUser.uid, "friends", fromUserId), {
        userId: fromUserId,
        addedAt: serverTimestamp()
      }),
      setDoc(doc(db, "users", fromUserId, "friends", auth.currentUser.uid), {
        userId: auth.currentUser.uid,
        addedAt: serverTimestamp()
      }),
      deleteDoc(requestRef)
    ]);

    return { success: true };
  } catch (error) {
    console.error("Error accepting friend request:", error);
    throw error;
  }
}

export async function declineFriendRequest(requestId) {
  try {
    const requestRef = doc(db, "users", auth.currentUser.uid, "friendRequests", requestId);
    await deleteDoc(requestRef);
    return { success: true };
  } catch (error) {
    console.error("Error declining friend request:", error);
    throw error;
  }
}

export const addUserBonus = async (bonusData) => {
  try {
    const userBonusesRef = collection(db, 'userBonuses');
    const q = query(
      userBonusesRef, 
      where('userId', '==', bonusData.userId),
      where('bankId', '==', bonusData.bankId),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      throw new Error('Bonus already exists for this bank');
    }

    return await addDoc(userBonusesRef, {
      ...bonusData,
      createdAt: new Date(),
      updatedAt: new Date(),
      status: 'active'
    });
  } catch (error) {
    console.error('Error adding bonus:', error);
    throw error;
  }
};

export function useUserBonus(bankId) {
  const auth = useAuth();
  
  return useQuery(
    ['userBonus', bankId],
    async () => {
      if (!auth.user) return null;
      
      const q = query(
        collection(db, 'userBonuses'),
        where('userId', '==', auth.user.uid),
        where('bankId', '==', bankId),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    },
    {
      enabled: !!auth.user && !!bankId
    }
  );
}

export const updateUserBonus = async (bonusId, updateData) => {
  try {
    await updateDoc(doc(db, 'userBonuses', bonusId), {
      ...updateData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating bonus:', error);
    throw error;
  }
};

export const deleteUserBonus = async (bonusId) => {
  try {
    await deleteDoc(doc(db, 'userBonuses', bonusId));
  } catch (error) {
    console.error('Error deleting bonus:', error);
    throw error;
  }
};

export const resetUserBonus = async (bonusId) => {
  try {
    await deleteDoc(doc(db, 'userBonuses', bonusId));
    return true;
  } catch (error) {
    console.error('Error resetting bonus:', error);
    throw error;
  }
};

export const createReferralInvite = async (toEmail) => {
  try {
    const referralCode = generateReferralCode();
    const fromUserId = auth.currentUser.uid;
    
    // Create pending invite with referral code
    const inviteRef = await addDoc(collection(db, 'pendingInvites'), {
      fromUserId,
      toEmail: toEmail.toLowerCase(),
      fromUserEmail: auth.currentUser.email,
      referralCode,
      createdAt: serverTimestamp(),
      status: 'pending'
    });

    // Send email with signup link including referral code
    const signupLink = `${window.location.origin}/signup?ref=${referralCode}`;
    const sendReferralEmail = httpsCallable(functions, 'sendReferralEmail');
    await sendReferralEmail({ 
      toEmail: toEmail.toLowerCase(),
      signupLink
    });

    return { success: true, inviteId: inviteRef.id };
  } catch (error) {
    console.error('Error creating referral:', error);
    throw error;
  }
};

// Helper function to generate unique referral code
const generateReferralCode = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

// Function to check referral code during signup
export const checkReferralCode = async (referralCode) => {
  try {
    const invitesRef = collection(db, 'pendingInvites');
    const q = query(invitesRef, where('referralCode', '==', referralCode), limit(1));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  } catch (error) {
    console.error('Error checking referral:', error);
    throw error;
  }
};

export function useUserData(userId) {
  return useQuery(
    ['user', userId],
    async () => {
      if (!userId) return null;
      const userDoc = await getDoc(doc(db, 'users', userId));
      return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
    },
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  );
}

export function useComments(bankId, sortBy = 'votes') {
  return useQuery(
    ['comments', bankId, sortBy],
    async () => {
      if (!bankId) return [];
      const q = query(
        collection(db, 'banks', bankId, 'comments'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const allComments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        replies: [],
        upvotes: doc.data().upvotes || 0,
        downvotes: doc.data().downvotes || 0,
        voteScore: (doc.data().upvotes || 0) - (doc.data().downvotes || 0)
      }));

      // Organize comments into parent-child structure
      const commentMap = {};
      const topLevelComments = [];

      // First, create a map of all comments
      allComments.forEach(comment => {
        commentMap[comment.id] = comment;
      });

      // Then, organize them into parent-child relationships
      allComments.forEach(comment => {
        if (comment.parentId && commentMap[comment.parentId]) {
          // This is a reply, add it to parent's replies
          commentMap[comment.parentId].replies.push(comment);
        } else {
          // This is a top-level comment
          topLevelComments.push(comment);
        }
      });

      // Sort replies by createdAt ascending (oldest first)
      topLevelComments.forEach(comment => {
        comment.replies.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return aTime - bTime;
        });
      });

      // Sort top-level comments based on sortBy parameter
      if (sortBy === 'votes') {
        topLevelComments.sort((a, b) => {
          const scoreA = a.voteScore;
          const scoreB = b.voteScore;
          if (scoreB !== scoreA) return scoreB - scoreA;
          // If scores are equal, sort by most recent
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      } else if (sortBy === 'recent') {
        topLevelComments.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      } else if (sortBy === 'oldest') {
        topLevelComments.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return aTime - bTime;
        });
      }

      return topLevelComments;
    },
    {
      enabled: !!bankId
    }
  );
}

export async function addComment(bankId, commentData) {
  const commentsRef = collection(db, 'banks', bankId, 'comments');
  return addDoc(commentsRef, {
    ...commentData,
    createdAt: serverTimestamp(),
    upvotes: 0,
    downvotes: 0
  });
}

// Delete a comment (admin only)
export async function deleteComment(bankId, commentId) {
  const commentRef = doc(db, 'banks', bankId, 'comments', commentId);
  return deleteDoc(commentRef);
}

// Update a comment
export async function updateComment(bankId, commentId, text) {
  const commentRef = doc(db, 'banks', bankId, 'comments', commentId);
  return updateDoc(commentRef, {
    text,
    updatedAt: serverTimestamp()
  });
}

// Check if user is muted
export async function isUserMuted(userId) {
  try {
    const mutedRef = doc(db, 'mutedUsers', userId);
    const mutedDoc = await getDoc(mutedRef);
    return mutedDoc.exists();
  } catch (error) {
    console.error('Error checking muted status:', error);
    return false;
  }
}

// Mute a user (admin only)
export async function muteUser(userId, adminId) {
  const mutedRef = doc(db, 'mutedUsers', userId);
  return setDoc(mutedRef, {
    mutedBy: adminId,
    mutedAt: serverTimestamp()
  });
}

// Unmute a user (admin only)
export async function unmuteUser(userId) {
  const mutedRef = doc(db, 'mutedUsers', userId);
  return deleteDoc(mutedRef);
}

// Get user's muted status
export function useUserMutedStatus(userId) {
  return useQuery(
    ['mutedStatus', userId],
    async () => {
      if (!userId) return false;
      return await isUserMuted(userId);
    },
    {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5 // 5 minutes
    }
  );
}

// Vote on a comment
export async function voteComment(bankId, commentId, userId, voteType) {
  const voteRef = doc(db, 'banks', bankId, 'comments', commentId, 'votes', userId);
  const commentRef = doc(db, 'banks', bankId, 'comments', commentId);

  // Get current vote
  const voteDoc = await getDoc(voteRef);
  const currentVote = voteDoc.exists() ? voteDoc.data().type : null;

  // Get current comment data
  const commentDoc = await getDoc(commentRef);
  const commentData = commentDoc.data();
  const upvotes = commentData.upvotes || 0;
  const downvotes = commentData.downvotes || 0;

  let newUpvotes = upvotes;
  let newDownvotes = downvotes;

  if (currentVote === voteType) {
    // Remove vote
    await deleteDoc(voteRef);
    if (voteType === 'upvote') {
      newUpvotes = Math.max(0, upvotes - 1);
    } else {
      newDownvotes = Math.max(0, downvotes - 1);
    }
  } else {
    // Add or change vote
    await setDoc(voteRef, { type: voteType, votedAt: serverTimestamp() });

    if (currentVote === 'upvote' && voteType === 'downvote') {
      newUpvotes = Math.max(0, upvotes - 1);
      newDownvotes = downvotes + 1;
    } else if (currentVote === 'downvote' && voteType === 'upvote') {
      newDownvotes = Math.max(0, downvotes - 1);
      newUpvotes = upvotes + 1;
    } else if (!currentVote) {
      if (voteType === 'upvote') {
        newUpvotes = upvotes + 1;
      } else {
        newDownvotes = downvotes + 1;
      }
    }
  }

  // Update comment vote counts
  await updateDoc(commentRef, {
    upvotes: newUpvotes,
    downvotes: newDownvotes
  });

  return { upvotes: newUpvotes, downvotes: newDownvotes };
}

// Get user's vote on a comment
export async function getUserCommentVote(bankId, commentId, userId) {
  if (!userId) return null;
  try {
    const voteRef = doc(db, 'banks', bankId, 'comments', commentId, 'votes', userId);
    const voteDoc = await getDoc(voteRef);
    return voteDoc.exists() ? voteDoc.data().type : null;
  } catch (error) {
    console.error('Error getting user vote:', error);
    return null;
  }
}
